import React from 'react'
import {BsCalendar2Date, BsCalendarCheck, BsCalendarX} from 'react-icons/bs'
import {IoLinkOutline, IoOpenOutline} from 'react-icons/io5'
import {formatSimplifyDate, isMidnightTime, isSameDay} from '../../helpers/dateUtils'
import getTitle from '../../helpers/getTitle'

// Màu ngữ nghĩa CỐ ĐỊNH cho khối thời gian (xanh=bắt đầu, đỏ=kết thúc) - ĐỘC LẬP với TYPE_COLORS
// (màu theo LOẠI thông báo, vd WEDDING hồng) vì đây là 2 khái niệm màu khác nhau: trước đây dùng
// chung colors.timeBg/timeText khiến khối thời gian ở course_class (luôn rơi về TYPE_COLORS.DEFAULT,
// xám mờ) không lên màu xanh/đỏ như mockup. `label` = màu nhãn phụ (BẮT ĐẦU/KẾT THÚC/CẢ NGÀY) -
// tách riêng field này (đậm hơn bg, nhạt hơn text) để nhãn KHÔNG còn xám phẳng lạc tông như trước,
// ăn theo đúng màu chủ đề của khối (xanh lá/đỏ/xanh dương).
const TIME_TONES = {
    start: {bg: '#ECFDF5', icon: '#059669', text: '#065F46', label: '#059669'},
    end: {bg: '#FFF1F2', icon: '#E11D48', text: '#9F1239', label: '#E11D48'},
    neutral: {bg: '#EFF6FF', icon: '#2563EB', text: '#1E40AF', label: '#2563EB'},
}

const AnnouncementTimeBox = ({icon: Icon, label, value, tone}) => value && (
    <div
        style={{background: tone.bg, borderRadius: 10, padding: '8px 12px'}}
        className="flex items-center gap-2 flex-1 min-w-0">
        <Icon style={{fontSize: 15, color: tone.icon, flexShrink: 0}}/>
        <div className="flex flex-col min-w-0 leading-tight">
            {label && (
                <span style={{color: tone.label}} className="text-2xs font-semibold tracking-wide uppercase">
                    {label}
                </span>
            )}
            <span style={{color: tone.text}} className="font-bold text-sm capitalize truncate">
                {value}
            </span>
        </div>
    </div>
)

export const renderAnnouncementTimeSection = ({time_open, time_close, t}) => {
    const hasBoth = time_open && time_close
    const sameDay = hasBoth && isSameDay(time_open, time_close)
    // Cả 2 mốc đều 00:00 (không lưu giờ cụ thể) - sự kiện "cả ngày", hiện khoảng "00:00 → 00:00"
    // (như 2 mốc trùng nhau) không có ý nghĩa nên chỉ hiện 1 box ngày + nhãn "Cả ngày".
    const isAllDay = sameDay && isMidnightTime(time_open) && isMidnightTime(time_close)

    if (isAllDay) {
        return (
            <AnnouncementTimeBox
                tone={TIME_TONES.neutral}
                icon={BsCalendar2Date}
                label={t('all day')}
                value={formatSimplifyDate(time_open, 'fullShort')}
            />
        )
    }
    if (hasBoth && sameDay) {
        return (
            <div className="flex flex-wrap gap-2">
                <AnnouncementTimeBox
                    tone={TIME_TONES.neutral}
                    icon={BsCalendar2Date}
                    value={formatSimplifyDate(time_open, 'fullShort')}
                />
                <AnnouncementTimeBox
                    tone={TIME_TONES.neutral}
                    icon={BsCalendar2Date}
                    value={`${formatSimplifyDate(time_open, '', 'HH:mm')} → ${formatSimplifyDate(time_close, '', 'HH:mm')}`}
                />
            </div>
        )
    }
    if (hasBoth) {
        return (
            <div className="flex flex-col sm:flex-row gap-2">
                <AnnouncementTimeBox tone={TIME_TONES.start} icon={BsCalendarCheck} label={t('start')}
                                     value={formatSimplifyDate(time_open, 'fullTime')}/>
                <AnnouncementTimeBox tone={TIME_TONES.end} icon={BsCalendarX} label={t('end')}
                                     value={formatSimplifyDate(time_close, 'fullTime')}/>
            </div>
        )
    }
    const t0 = time_open || time_close
    const tone = time_open ? TIME_TONES.start : TIME_TONES.end
    const icon = time_open ? BsCalendarCheck : BsCalendarX
    const label = time_open ? t('start') : t('end')
    return (
        <AnnouncementTimeBox tone={tone} icon={icon} label={label} value={formatSimplifyDate(t0, 'fullTime')}/>
    )
}

// Nhãn loại thông báo (icon tròn màu + tên loại, vd "Thông báo chung"). Bạn góp ý "nhiều khối màu
// quá": badge trước đây có nền pill riêng (colors.bg) chồng thêm 1 vùng màu lên nền card + dải chip
// + khối message + khối thời gian. Giờ badge KHÔNG nền khi hiện in-flow - chỉ giữ icon tròn màu theo
// loại làm điểm nhận diện, chữ về tông trung tính (text-color-100). Biến thể `overlay` (đè lên ảnh)
// VẪN giữ nền trắng mờ vì cần tương phản để đọc trên ảnh.
export const AnnouncementTypeBadge = ({icon, label, colors, overlay = false}) => label && (
    <div
        className="inline-flex items-center gap-2 max-w-full"
        style={overlay ? {
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(6px)',
            borderRadius: 999,
            padding: '4px 12px 4px 4px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.13)',
        } : undefined}>
        <span
            style={{width: 24, height: 24, borderRadius: '50%', background: colors?.iconBg || '#94A3B8', fontSize: 13}}
            className="flex flex-center flex-shrink-0">
            {/* filter ép icon trắng bất kể icon gốc màu gì - 1 công thức duy nhất cho mọi icon. */}
            <span style={{filter: 'brightness(0) invert(1)', display: 'flex'}}>{icon}</span>
        </span>
        <span className="text-xs font-bold tracking-wide truncate text-color-100">
            {label}
        </span>
    </div>
)

// Khối nội dung thông báo (message body) - kiểu "trích dẫn" (viền trái + nghiêng), TÁI DÙNG đúng
// convention quote đã có sẵn trong app (DetailAskContent.js/BaseEmbed.js: border-l-2 + nghiêng),
// không bịa kiểu khung mới. Bạn góp ý "nhiều khối màu quá": bỏ nền fill (background-100) + bo góc
// phải, chỉ giữ viền trái theo màu type - còn 1 đường mảnh thay vì 1 mảng nền, nhường điểm nhấn màu
// duy nhất cho khối thời gian bên dưới.
export const AnnouncementMessage = ({content, colors}) => content && (
    <div
        style={{borderColor: colors?.border || '#E2E8F0'}}
        className="pl-3 border-l-2 text-sm text-color-200 italic leading-relaxed whitespace-pre-line">
        {content}
    </div>
)

// Nút mở link đính kèm (Zoom/tài liệu/mẫu trình bày...) - thay cho text link trơn trước đây, đúng
// mockup (nút bo viền + icon). KHÔNG có field backend nào lưu nhãn riêng theo ngữ cảnh (vd "Mở
// liên kết phòng Zoom" trong mockup) nên dùng chung nhãn t('open link') - key đã có sẵn.
export const AnnouncementLinkButton = ({shared_url, colors, t}) => shared_url && (
    <a
        href={shared_url} target="_blank" rel="noopener noreferrer"
        style={{borderColor: colors?.border, color: colors?.accent}}
        className="self-start px-3 py-2 flex items-center gap-2 border rounded-xl text-sm font-semibold no-underline hover:opacity-80 transition-opacity">
        <IoLinkOutline size={15}/>
        <span>{t('open link')}</span>
        <IoOpenOutline size={13} style={{opacity: 0.6}}/>
    </a>
)

// Chip ngữ cảnh (Lớp/Chuyên đề - Khóa/Khóa/Cộng đoàn/Nhóm) - theo owner_type, tái dùng ĐÚNG field đã
// có ở AnnouncementTransformer.php (course_class/course/organization/group + subject), KHÔNG suy diễn
// field mới. Field "Chuyên đề" (subject) chỉ áp dụng owner_type=course_class và chỉ hiện khi thật sự
// có gán môn (AnnouncementForm.js cho phép để trống). Trả {label, value} thuần - phần hiển thị do
// AnnouncementMetaLine lo (trước đây kèm `tone` màu riêng theo owner_type, đã bỏ: xem comment ở đó).
export const getAnnouncementContextChips = (item, t) => {
    const {owner_type, course_class, course, organization, group, subject} = item ?? {}
    const chips = []
    switch (owner_type) {
        case 'course_class':
            if (course_class) chips.push({label: t('class'), value: getTitle(course_class)})
            if (subject?.title) {
                const courseTitle = getTitle(course_class?.course)
                chips.push({
                    label: t('subject'),
                    value: courseTitle ? `${subject.title} (${courseTitle})` : subject.title,
                })
            }
            break
        case 'course':
            if (course) chips.push({label: t('course'), value: getTitle(course)})
            break
        case 'organization':
            if (organization) chips.push({label: t('organization'), value: getTitle(organization)})
            break
        case 'group':
            if (group) chips.push({label: t('group'), value: getTitle(group)})
            break
        default:
            break
    }
    return chips
}

// Dòng "Người nhận" (Gửi tới) - suy diễn text chung chung từ field ĐÃ CÓ, không bịa field targeting
// mới: course_class dùng lại ĐÚNG convention is_for_all/announcement_course_class_students đã áp
// dụng ở TeacherAnnouncements.js/StudentAnnouncements.js; course luôn toàn bộ học viên (khớp
// Announcement::sendAnnouncementEmails - course không có khái niệm chọn riêng); organization/group
// không có cơ chế thu hẹp đối tượng nên luôn là toàn bộ.
export const getAnnouncementAudienceText = (item, t) => {
    const {owner_type, is_for_all, announcement_course_class_students} = item ?? {}
    switch (owner_type) {
        case 'course_class': {
            const count = announcement_course_class_students?.length || 0
            if (count > 0) return t('{count} students', {count})
            if (is_for_all) return t('all students')
            return null
        }
        case 'course':
            return t('all students')
        case 'organization':
            return t('notify entire organization')
        case 'group':
            return t('for all members')
        default:
            return null
    }
}

// Dòng metadata ngữ cảnh (Lớp/Môn học/Người nhận) - GỘP các chip có nền (indigo/green/neutral) trước
// đây thành 1 dòng chữ xám phân tách bằng "·". Bạn góp ý "nhiều khối màu quá": mỗi chip là 1 mảng
// fill, xếp cạnh nhau + chồng lên nền card + khối message + khối thời gian làm rối thứ bậc. Metadata
// không phải nội dung chính nên hạ hẳn xuống text thường, nhường điểm nhấn màu duy nhất cho khối
// thời gian. Vẫn tái dùng getAnnouncementContextChips / getAnnouncementAudienceText, chỉ đổi cách
// render.
export const AnnouncementMetaLine = ({item, t}) => {
    const audience = getAnnouncementAudienceText(item, t)
    const parts = getAnnouncementContextChips(item, t).map(c => `${c.label}: ${c.value}`)
    if (audience) parts.push(`${t('recipients')}: ${audience}`)
    if (!parts.length) return null
    return (
        <div className="text-xs text-color-200 leading-relaxed">
            {parts.map((part, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <span className="opacity-40 mx-1.5">·</span>}
                    {part}
                </React.Fragment>
            ))}
        </div>
    )
}
